import { Box, Button, Stack, styled } from '@mui/material';
import { TFunction } from 'i18next';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { RhfTextField } from '@cnx-saas/common-fe';

export type ChangePasswordFormInput = {
  password: string;
  newPassword: string;
  confirmPassword: string;
};

type ChangePasswordFormProps = {
  onClose(): void;
  onSubmit(userInput: ChangePasswordFormInput): void;
};

export const ChangePasswordForm = ({
  onClose,
  onSubmit,
}: ChangePasswordFormProps) => {
  const { t } = useTranslation();

  const { control, handleSubmit } = useForm<ChangePasswordFormInput>({
    resolver: yupResolver(changePassowrdFormSchema(t)),
    defaultValues: {
      password: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  return (
    <Root onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
        <RhfTextField
          control={control}
          name="password"
          type="password"
          label={t('userManagement.formLabel.currentPassword', {
            defaultValue: 'Current Password',
          })}
        />
        <RhfTextField
          control={control}
          name="newPassword"
          type="password"
          label={t('registration.formLabel.newPassword', {
            defaultValue: 'New Password',
          })}
        />
        <RhfTextField
          control={control}
          type="password"
          name="confirmPassword"
          label={t('registration.formLabel.confirmPassword', {
            defaultValue: 'Confirm Password',
          })}
        />
        <Stack flexDirection="row" justifyContent="end" gap={1}>
          <Button variant="outlined" onClick={onClose}>
            {t('registration.formLabel.cancel', {
              defaultValue: 'Cancel',
            })}
          </Button>
          <Button type="submit">
            {t('registration.formLabel.save', { defaultValue: 'Save' })}
          </Button>
        </Stack>
      </Box>
    </Root>
  );
};

export const Root = styled('form')({
  width: '100%',
});

export const changePassowrdFormSchema = (t: TFunction) => {
  const fieldRequiredMessage = t(
    'registration.form.validation-error.required',
    {
      defaultValue: 'This field is required.',
    },
  );
  const passwordValidationMessage = {
    minLength: t('registration.form.validation-error.minLength', {
      defaultValue: 'Password must be at least 8 characters long.',
    }),
    hasUpperCase: t('registration.form.validation-error.hasUpperCase', {
      defaultValue: 'Password must contain at least one uppercase letter.',
    }),
    hasLowerCase: t('registration.form.validation-error.hasLowerCase', {
      defaultValue: 'Password must contain at least one lowercase letter.',
    }),
    hasDigit: t('registration.form.validation-error.hasDigit', {
      defaultValue: 'Password must contain at least one digit.',
    }),
    hasSpecialChar: t('registration.form.validation-error.hasSpecialChar', {
      defaultValue: 'Password must contain at least one special character.',
    }),
  };

  return yup.object().shape({
    password: yup
      .string()
      .required(fieldRequiredMessage)
      .test('minLength', passwordValidationMessage.minLength, (value) =>
        value ? value.length >= 8 : false,
      )
      .test('hasUpperCase', passwordValidationMessage.hasUpperCase, (value) =>
        value ? /[A-Z]/.test(value) : false,
      )
      .test('hasLowerCase', passwordValidationMessage.hasLowerCase, (value) =>
        value ? /[a-z]/.test(value) : false,
      )
      .test('hasDigit', passwordValidationMessage.hasDigit, (value) =>
        value ? /\d/.test(value) : false,
      )
      .test(
        'hasSpecialChar',
        passwordValidationMessage.hasSpecialChar,
        (value) => (value ? /[!@#$%^&*(),.?":{}|<>]/.test(value) : false),
      ),
    newPassword: yup
      .string()
      .notOneOf(
        [yup.ref('password')],
        t('registration.form.validation-error.passwordIsSame', {
          defaultValue: 'Cannnot use same password.',
        }),
      )
      .required(fieldRequiredMessage)
      .test('minLength', passwordValidationMessage.minLength, (value) =>
        value ? value.length >= 8 : false,
      )
      .test('hasUpperCase', passwordValidationMessage.hasUpperCase, (value) =>
        value ? /[A-Z]/.test(value) : false,
      )
      .test('hasLowerCase', passwordValidationMessage.hasLowerCase, (value) =>
        value ? /[a-z]/.test(value) : false,
      )
      .test('hasDigit', passwordValidationMessage.hasDigit, (value) =>
        value ? /\d/.test(value) : false,
      )
      .test(
        'hasSpecialChar',
        passwordValidationMessage.hasSpecialChar,
        (value) => (value ? /[!@#$%^&*(),.?":{}|<>]/.test(value) : false),
      ),
    confirmPassword: yup
      .string()
      .oneOf(
        [yup.ref('newPassword')],
        t('registration.form.validation-error.passwordMatch', {
          defaultValue: 'Passwords must match.',
        }),
      )
      .required(fieldRequiredMessage),
  });
};
